import type { ChatHistoryResponse } from "types";

export type ChatHistoryCsvLabels = {
  session: string;
  date: string;
  status: string;
  question: string;
  answer: string;
};

function escapeCsvCell(value: string): string {
  return `"${value.replaceAll('"', '""')}"`;
}

/**
 * Serialize chat history sessions into CSV text, one row per question/answer turn.
 * Columns follow the provided localized labels.
 */
export function chatHistoryToCsv(
  history: ChatHistoryResponse[],
  labels: ChatHistoryCsvLabels
): string {
  const header = [
    labels.session,
    labels.date,
    labels.status,
    labels.question,
    labels.answer,
  ];
  const rows = history.flatMap((session) =>
    session.history.map((turn) => [
      session.session_id,
      turn.timestamp,
      turn.status,
      turn.question,
      turn.answer,
    ])
  );
  return [header, ...rows]
    .map((row) =>
      row.map((cell) => escapeCsvCell(String(cell ?? ""))).join(",")
    )
    .join("\r\n");
}

/** Trigger a browser download of CSV text as a UTF-8 file (BOM included for Excel). */
export function downloadCsv(csv: string, filename: string): void {
  // Prepend a UTF-8 BOM so Excel detects the encoding correctly.
  const blob = new Blob(["﻿" + csv], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke on the next tick so the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
